import { useState } from 'react';
import type { AtHomeRevenue } from '@aithora/shared';
import type { OpCtx } from './OperatorHome';
import { tierColor } from './OpMembers';
import { Act, chip16, D, S } from './ui';
import { BuildLabel, useBuildLabels, useCustomerText } from '../../ui/buildLabels';

// Revenue (design T12060-12174, hOpMoney). Reads the PROPOSED GET /athome/revenue:
// each member's plan, price and devices; the attach and escalation lines are the pricing
// plan (MODELED); every lens, the thin spots and the per-member list are arithmetic
// over those rows, exactly as the design computes them.

type M = AtHomeRevenue['members'][number];
const PAL = ['#2F5C97', '#2F8A6F', '#5B6B7C', '#5B6B7C', '#B3261E', '#54606C'];
const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const HEAD = 'flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#22272E;';
const growW = (w: number, col: string, delay = 0) => 'display:block;height:100%;width:' + w + '%;border-radius:3px;transform-origin:left;animation:growW .6s cubic-bezier(.22,.61,.36,1) forwards;' + (delay ? 'animation-delay:' + delay + 'ms;' : '') + 'background:' + col + ';';

/** Each member's monthly revenue and the caseload's total (shared with the header's MRR). */
export function revenueOf(r: AtHomeRevenue) {
  const revOf = (m: M) => m.priceUsd + Math.max(0, m.devices - 1) * r.attachUsd + (m.plan === 'Home+' ? r.escalationUsd : 0);
  const total = r.members.reduce((n, m) => n + revOf(m), 0);
  return { revOf, total, arpu: r.members.length ? total / r.members.length : 0 };
}

const tierKey = (m: M) => `${m.tier.charAt(0)}${m.tier.slice(1).toLowerCase()} risk`;
const KEYER: Record<string, ((m: M) => string) | undefined> = { user: tierKey, device: (m) => m.brand };

export function OpRevenue({ c }: { c: OpCtx }) {
  const buildLabels = useBuildLabels();
  const plain = useCustomerText();
  const r = c.o.revenue.data;
  const [sel, setSel] = useState<string | null>(null);
  if (!r) {
    return (
      <D css="display:flex;flex-direction:column;gap:10px;min-width:0;padding-top:8px;font-size:16px;color:#54606C;">
        {c.o.revenue.error ? plain('Revenue is not connected yet: waiting on GET /athome/revenue (plans, prices and devices per member). No figure is shown rather than a guess.') : ''}
      </D>
    );
  }
  const { revOf, total, arpu } = revenueOf(r);
  const members = r.members;
  const ATTACH = r.attachUsd;
  const ESC = r.escalationUsd;
  const lens = (id: string, name: string, q: string, g: Record<string, number>, note: string) => {
    const keys = Object.keys(g).sort((a, b) => g[b] - g[a]);
    const max = Math.max(...keys.map((k) => g[k]), 1);
    return { id, name, q, note, rows: keys.map((k, i) => ({ k, v: g[k], i, max })) };
  };
  const groupBy = (keyer: (m: M) => string) => {
    const g: Record<string, number> = {};
    members.forEach((m) => {
      const k = keyer(m);
      g[k] = (g[k] ?? 0) + revOf(m);
    });
    return g;
  };
  const selLens = sel ? sel.split(':')[0] : null;
  const selKey = sel ? sel.slice(sel.indexOf(':') + 1) : null;
  const shown = members.filter((m) => !sel || !selLens || !KEYER[selLens] || KEYER[selLens]!(m) === selKey);
  const perM = shown.slice().sort((a, b) => revOf(b) - revOf(a));
  const maxM = Math.max(...members.map(revOf), 1);
  const shownTotal = shown.reduce((n, m) => n + revOf(m), 0);
  const thin = members.filter((m) => revOf(m) < arpu * 0.85);
  const two = members.filter((m) => m.devices <= 2);
  const homeOnly = members.filter((m) => m.plan === 'Home');
  const tMax = Math.max(...r.trend.map((t) => t.usd), 1);
  const moveMax = Math.max(...r.moved.map((m) => Math.abs(m.usd)), 1);
  const lenses = [
    lens('user', 'By user type', 'which kind of member pays us · tap to filter', groupBy(tierKey), 'Risk tier is the strongest revenue signal we have, higher-acuity members hold more devices and sit on Home+. Growth follows acuity, not headcount.'),
    lens('device', 'By device', 'which hardware carries the money · tap to filter', groupBy((m) => m.brand), 'Attach is billed per paired device beyond the hub, so brand mix is really device-count mix. Apple and Medical Guardian households run the most hardware.'),
    lens('line', 'By line', 'what we actually charge for', {
      Subscription: members.reduce((n, m) => n + m.priceUsd, 0),
      'Device attach': members.reduce((n, m) => n + Math.max(0, m.devices - 1) * ATTACH, 0),
      'Escalation service': members.filter((m) => m.plan === 'Home+').length * ESC,
    }, 'Subscription is the floor. Attach and escalation are the only lines that move without adding a member. That is where the next dollar comes from.'),
  ];

  return (
    <D css="display:flex;flex-direction:column;gap:10px;min-width:0;">
      <D css="display:flex;align-items:flex-end;gap:20px;flex-wrap:wrap;row-gap:9px;min-width:0;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.12);">
        <S css="flex:0 0 auto;min-width:0;">
          <S css="display:block;font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:600;">Monthly recurring {'·'} this caseload</S>
          <S css="display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;">
            <S css="font-size:34px;font-weight:700;letter-spacing:-.035em;line-height:1.05;font-variant-numeric:tabular-nums;color:#22272E;">{money(total)}</S>
            <S css={'font-size:16px;font-weight:700;color:' + (r.deltaUsd >= 0 ? '#1F6B52' : '#B3261E') + ';'}>{`${r.deltaUsd >= 0 ? '+' : '−'}${money(Math.abs(r.deltaUsd))} vs last month`}</S>
          </S>
        </S>
        {[
          ['ARPU', money(arpu), 'per member, per month'],
          ['Earning devices', String(members.reduce((n, m) => n + Math.max(0, m.devices - 1), 0)), `paired beyond the hub at ${money(ATTACH)}`],
          ['On Home+', `${Math.round((members.filter((m) => m.plan === 'Home+').length / Math.max(1, members.length)) * 100)}%`, 'of the caseload'],
          ['Annualized', money(total * 12), 'if nothing changes'],
        ].map(([k, v, note]) => (
          <S key={k} css="flex:0 1 auto;min-width:0;display:block;">
            <S css="display:block;font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:600;">{k}</S>
            <S css="display:block;font-size:19px;font-weight:700;letter-spacing:-.025em;line-height:1.15;font-variant-numeric:tabular-nums;color:#22272E;">{v}</S>
            <S css="display:block;font-size:14px;color:#54606C;line-height:1.3;">{note}</S>
          </S>
        ))}
        <S css="flex:1 1 0;min-width:0;" />
        <BuildLabel><S css={chip16('#54606C', 'rgba(34,39,46,.09)')}>MODELED</S></BuildLabel>
      </D>

      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:12px 26px;min-width:0;align-items:start;">
        {lenses.map((L) => (
          <D key={L.id} css="min-width:0;">
            <D css="display:flex;align-items:baseline;gap:7px;flex-wrap:wrap;padding-bottom:4px;min-width:0;">
              <S css="flex:0 0 auto;font-size:16px;font-weight:700;letter-spacing:-.01em;color:#22272E;">{L.name}</S>
              <S css="flex:1 1 110px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">{L.q}</S>
            </D>
            {L.rows.map((row) => {
              const tag = `${L.id}:${row.k}`;
              const on = sel === tag;
              const dim = sel && !on && KEYER[L.id];
              const col = PAL[row.i % PAL.length];
              const rowCss = 'display:flex;align-items:center;gap:7px;padding:2px 4px;border-radius:5px;min-width:0;text-decoration:none;color:inherit;transition:opacity .18s ease,background .18s ease;' + (on ? 'background:rgba(47,92,151,.12);' : '') + (dim ? 'opacity:.38;' : '');
              const cells = (
                <>
                  <S css={'flex:0 0 8px;width:8px;height:8px;border-radius:2px;background:' + col + ';'} />
                  <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;">{row.k}</S>
                  <S css="flex:0 0 46px;height:6px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;"><S css={growW(Math.round((row.v / row.max) * 100), col, row.i * 45)} /></S>
                  <S css="flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;">{money(row.v)}</S>
                  <S css="flex:0 0 28px;text-align:right;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;">{`${Math.round((row.v / Math.max(1, total)) * 100)}%`}</S>
                </>
              );
              // A line of what we charge (Subscription, Device attach, Escalation service) is
              // not a group of members, so it is a figure to read, not a filter to press.
              if (!KEYER[L.id]) return <D key={row.k} css={rowCss} testId={`rev-line-${L.id}`}>{cells}</D>;
              return (
                <Act key={row.k} css={rowCss} onClick={() => setSel(on ? null : tag)}>
                  {cells}
                </Act>
              );
            })}
            <D css="font-size:14px;color:#54606C;line-height:1.35;margin-top:4px;text-wrap:pretty;">{L.note}</D>
          </D>
        ))}
      </D>

      <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:12px 26px;min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);align-items:start;">
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:6px;">
            <S css={HEAD}>MRR trend</S>
            <S css="flex:1 1 140px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">{`${r.trend.length} months · modeled from account start dates · today is the last bar`}</S>
          </D>
          <S css="display:flex;align-items:flex-end;gap:3px;height:48px;min-width:0;">
            {r.trend.map((t, i) => (
              <S key={t.month} title={`${t.month} · ${money(t.usd)}`} css={'flex:1 1 0;min-width:4px;height:' + Math.max(4, Math.round((t.usd / tMax) * 46)) + 'px;border-radius:2px 2px 0 0;transform-origin:bottom;animation:growH .55s cubic-bezier(.22,.61,.36,1) both;animation-delay:' + i * 35 + 'ms;background:' + (i === r.trend.length - 1 ? '#2F5C97' : 'rgba(47,92,151,.36)') + ';'} />
            ))}
          </S>
          <S css="display:flex;justify-content:space-between;font-size:14px;color:#54606C;margin-top:3px;">
            {r.trend.map((t) => (
              <S key={t.month} css="flex:1 1 0;text-align:center;">{t.month}</S>
            ))}
          </S>
        </D>
        <D css="min-width:0;">
          <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
            <S css={HEAD}>What moved</S>
            <S css="flex:1 1 110px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">{buildLabels ? <>what changed this month, in dollars {'·'} MODELED</> : 'what changed this month, in dollars, estimated'}</S>
            <S css="flex:0 0 auto;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:#1F6B52;">{(() => { const net = r.moved.reduce((n, m) => n + m.usd, 0); return `${net >= 0 ? '+' : '−'}${money(Math.abs(net))}`; })()}</S>
          </D>
          {r.moved.map((m) => (
            <S key={m.k} css="display:flex;align-items:center;gap:8px;padding:2px 0;min-width:0;">
              <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;">{m.k}</S>
              <S css="flex:0 0 auto;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;">{m.n}</S>
              <S css="flex:0 0 60px;height:6px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;"><S css={growW(Math.round((Math.abs(m.usd) / moveMax) * 100), m.usd > 0 ? '#2F8A6F' : '#B3261E')} /></S>
              <S css={'flex:0 0 52px;text-align:right;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:' + (m.usd > 0 ? '#1F6B52' : '#B3261E') + ';'}>{`${m.usd > 0 ? '+' : '−'}${money(Math.abs(m.usd))}`}</S>
            </S>
          ))}
        </D>
      </D>

      <D css="min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);">
        <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
          <S css={HEAD.replace('#22272E', '#4A5866')}>Where we are thin</S>
          <S css="flex:1 1 170px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">revenue we are not earning today, with the reason. WHAT-IF, not booked.</S>
        </D>
        <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:6px;min-width:0;">
          {[
            ['Below ARPU', `${thin.length} of ${members.length} members`, `${money(thin.reduce((n, m) => n + (arpu - revOf(m)), 0))}/mo`, 'the gap if each one reached the average'],
            ['Two devices or fewer', `${two.length} members`, `${money(two.length * ATTACH * 2)}/mo`, 'unearned attach at two more devices each'],
            ['Still on Home', `${homeOnly.length} members`, `${money(homeOnly.length * (89 - 66 + ESC))}/mo`, 'if every one moved to Home+ with escalation'],
          ].map(([k, n, v, note]) => (
            <D key={k} css="box-sizing:border-box;padding:8px 11px;border-radius:9px;background:rgba(91,107,124,.07);border:1px solid rgba(91,107,124,.26);min-width:0;">
              <S css="display:flex;align-items:baseline;gap:8px;min-width:0;">
                <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:700;overflow-wrap:break-word;">{k}</S>
                <S css="flex:0 0 auto;font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;color:#4A5866;">{v}</S>
              </S>
              <S css="display:block;font-size:14px;color:#3C454F;line-height:1.35;margin-top:2px;text-wrap:pretty;">
                <b style={{ fontWeight: 700 }}>{n}</b> {'·'} {note}
              </S>
            </D>
          ))}
        </D>
      </D>

      <D css="min-width:0;padding-top:8px;border-top:1px solid rgba(34,39,46,.12);">
        <D css="display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:5px;">
          <S css={HEAD}>Revenue per member</S>
          <S css="flex:1 1 170px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;">{`${sel ? `filtered to ${selKey} · ` : 'every member, most to least · '}amber sits below ARPU · tap to open the account`}</S>
          {sel && (
            <S css="flex:0 0 auto;display:inline-flex;align-items:center;gap:8px;padding:3px 6px 3px 10px;border-radius:20px;background:rgba(47,92,151,.12);border:1px solid rgba(47,92,151,.3);animation:riseIn .3s cubic-bezier(.22,.61,.36,1);">
              <S css="font-size:14px;font-weight:700;color:#264C7E;white-space:nowrap;">{selKey}</S>
              <S css="font-size:14px;color:#3C454F;white-space:nowrap;">{`${shown.length}${shown.length === 1 ? ' member · ' : ' members · '}${money(shownTotal)}/mo · ${Math.round((shownTotal / Math.max(1, total)) * 100)}% of revenue`}</S>
              <Act css="display:inline-flex;align-items:center;justify-content:center;width:17px;height:17px;border-radius:50%;background:rgba(47,92,151,.22);font-size:14px;font-weight:700;color:#264C7E;text-decoration:none;" onClick={() => setSel(null)} label="Clear the filter">
                {'×'}
              </Act>
            </S>
          )}
        </D>
        <D css="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:0 24px;min-width:0;">
          {perM.map((m) => {
            const tc = tierColor(m.tier);
            return (
              <Act key={m.residentId} css="display:flex;align-items:center;gap:8px;padding:2px 4px;border-radius:5px;min-width:0;text-decoration:none;color:inherit;" onClick={() => c.manage(m.residentId)}>
                <S css="flex:1 1 0;min-width:0;font-size:14px;font-weight:600;overflow-wrap:break-word;">{m.name}</S>
                <S css={chip16(tc, tc + '22')}>{m.tier}</S>
                <S css="flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;">{`${m.plan} · ${m.devices} dev`}</S>
                <S css="flex:0 0 54px;height:6px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;"><S css={growW(Math.round((revOf(m) / maxM) * 100), revOf(m) < arpu * 0.85 ? '#5B6B7C' : '#2F5C97')} /></S>
                <S css="flex:0 0 auto;font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;">{money(revOf(m))}</S>
              </Act>
            );
          })}
        </D>
      </D>
      <D css="font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;">{!buildLabels ? `The attach fee (${money(ATTACH)} per paired device beyond the hub) and the escalation line (${money(ESC)}, Home+ only) are estimates from the pricing plan, not billed revenue. No reimbursement or rebate revenue is counted here.` : `Subscription prices are ${r.real ? 'LIVE from' : 'SAMPLE, not'} the account records. The attach fee (${money(ATTACH)} per paired device beyond the hub) and the escalation line (${money(ESC)}, Home+ only) are MODELED. That is the pricing plan, not billed revenue. Where we are thin is WHAT-IF. No reimbursement or rebate revenue is counted here.`}</D>
    </D>
  );
}
